// SP-4 WS-2 — the compute-and-compare surface: a colleague checking the
// math. "Contract says $450 (3 hrs × $150). Invoice says $520. Difference:
// $70." Four states: match (green), variance (amber — issuing requires an
// acknowledged reason), no-terms (informational, links to the Contract
// Terms tab), needs-quantity (honest "can't verify"). Never blocks
// silently, never auto-corrects.

import { Link } from "react-router-dom";
import { StatusBadge } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/utils/datetime";
import type { ContractComparisonDto } from "@/api/portfolioAdmin/types";
import { fmtMoney } from "../billing/vendorBillingFormat";

/**
 * "per terms effective Jul 1, 2026" — the basis line.
 *
 * A reviewer approving a variance is signing off on a number, and "contract
 * says $450" is not something you can check without knowing WHICH terms said
 * so. Rendered as a wall-calendar date: an effective date is a date, not an
 * instant, so it must not shift with the viewer's timezone.
 */
function TermBasisLine({ comparison }: { comparison: ContractComparisonDto }) {
  if (!comparison.termEffectiveFrom) return null;
  return (
    <span
      className="block text-caption text-gray-mid mt-space-1"
      data-testid="contract-check-term-basis"
    >
      Per terms effective {formatCalendarDate(comparison.termEffectiveFrom)}
      {comparison.asOf
        ? ` · checked against the ${formatCalendarDate(comparison.asOf)} billing period`
        : ""}
    </span>
  );
}

export function ContractCheckChip({
  comparison,
}: {
  comparison: ContractComparisonDto | null | undefined;
}) {
  if (!comparison) return null;
  switch (comparison.status) {
    case "match":
      return (
        <StatusBadge tone="ok" data-testid="contract-check-chip">
          Matches contract
        </StatusBadge>
      );
    case "variance":
      return (
        <StatusBadge tone="warn" data-testid="contract-check-chip">
          Differs from contract
        </StatusBadge>
      );
    case "needs-quantity":
      return (
        <StatusBadge tone="muted" data-testid="contract-check-chip">
          Can&apos;t verify
        </StatusBadge>
      );
    default:
      return (
        <StatusBadge tone="muted" data-testid="contract-check-chip">
          No terms on file
        </StatusBadge>
      );
  }
}

export function ContractCheckPanel({
  comparison,
  vendorId,
}: {
  comparison: ContractComparisonDto | null | undefined;
  /** When set, the no-terms state links to the vendor's Contract Terms tab. */
  vendorId?: string;
}) {
  if (!comparison) return null;

  if (comparison.status === "no-terms") {
    const period = comparison.asOf ? formatCalendarDate(comparison.asOf) : null;
    return (
      <div
        className="rounded-md border border-border-light bg-white-warm p-space-3"
        data-testid="contract-check-panel"
      >
        <p className="text-small text-gray-mid m-0">
          No contract terms were in effect
          {period ? ` for the ${period} billing period` : " for this vendor"}, so
          the amount can&apos;t be checked automatically. Terms added later apply
          from their own effective date — they are not applied backwards over
          work that predates them.{" "}
          {vendorId ? (
            <Link
              to={`/portfolio-admin/vendors/${vendorId}?tab=contract-terms`}
              className="text-gold-shadow underline hover:text-onyx"
              data-testid="contract-check-add-terms-link"
            >
              Add them on the Contract Terms tab.
            </Link>
          ) : (
            "Add them on the vendor's Contract Terms tab."
          )}
        </p>
      </div>
    );
  }

  if (comparison.status === "needs-quantity") {
    const missing =
      comparison.basis === "Hourly"
        ? "the report doesn't say how many hours were worked"
        : "the report doesn't include the transaction value";
    return (
      <div
        className="rounded-md border border-border-light bg-white-warm p-space-3"
        data-testid="contract-check-panel"
      >
        <p className="text-small text-gray-mid m-0">
          Can&apos;t verify: the contract is {comparison.termSummary}, but{" "}
          {missing}. Review the amount manually or ask the vendor to include
          it next time.
          <TermBasisLine comparison={comparison} />
        </p>
      </div>
    );
  }

  const isMatch = comparison.status === "match";
  return (
    <div
      className={
        "rounded-md border p-space-3 " +
        (isMatch
          ? "border-status-success bg-status-success/10"
          : "border-status-warning bg-status-warning/15")
      }
      data-testid="contract-check-panel"
    >
      <p className="text-small text-onyx m-0">
        Contract says{" "}
        <span className="font-semibold tabular-nums">
          {fmtMoney(comparison.expectedAmount ?? 0)}
        </span>
        {comparison.termSummary ? ` (${comparison.termSummary})` : ""}. Invoice
        says{" "}
        <span className="font-semibold tabular-nums">
          {fmtMoney(comparison.invoicedAmount)}
        </span>
        .{" "}
        {isMatch ? (
          <span className="font-medium">Matches the contract terms.</span>
        ) : (
          <span className="font-medium" data-testid="contract-check-delta">
            Difference: {fmtMoney(Math.abs(comparison.delta ?? 0))}{" "}
            {(comparison.delta ?? 0) > 0 ? "over" : "under"} the contract.
          </span>
        )}
        <TermBasisLine comparison={comparison} />
      </p>
    </div>
  );
}
